import { useMemo, useState } from 'react'
import {
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { PULSE_COLORS, QA_SCORE_PENALTIES, QA_SCORE_THRESHOLDS, SPRINT_END_DATES } from './pulseConfig.js'

// AI Sprint Analysis (robot emoji + modal) and PNG export are intentionally dropped
// from this port — the qa-live versions depend on /api/ai/sprint-analysis,
// ReactMarkdown, and html2canvas, none of which exist in jira-dashboard-app yet.
// All chart, overlay, metric, and correlation features are preserved.

function computeQaScore(sprint) {
    let score = 100
    score -= sprint.hotfixCount * QA_SCORE_PENALTIES.HOTFIX_RELEASE
    score -= sprint.patchCount * QA_SCORE_PENALTIES.PATCH_RELEASE
    const defectRate = sprint.completedCount > 0 ? sprint.bugsInSprint / sprint.completedCount : 0
    if (defectRate > QA_SCORE_PENALTIES.DEFECT_RATE_HIGH_THRESHOLD) {
        score -= QA_SCORE_PENALTIES.DEFECT_RATE_HIGH_PENALTY
    } else if (defectRate > QA_SCORE_PENALTIES.DEFECT_RATE_LOW_THRESHOLD) {
        score -= QA_SCORE_PENALTIES.DEFECT_RATE_LOW_PENALTY
    }
    const completionRate = sprint.committedCount > 0 ? sprint.completedCount / sprint.committedCount : 1
    if (completionRate < QA_SCORE_PENALTIES.COMPLETION_RATE_CRITICAL_THRESHOLD) {
        score -= QA_SCORE_PENALTIES.COMPLETION_RATE_CRITICAL_PENALTY
    } else if (completionRate < QA_SCORE_PENALTIES.COMPLETION_RATE_LOW_THRESHOLD) {
        score -= QA_SCORE_PENALTIES.COMPLETION_RATE_LOW_PENALTY
    }
    return Math.max(0, Math.round(score * 10) / 10)
}

function pearsonR(xs, ys) {
    const n = xs.length
    if (n < 3) return null
    const meanX = xs.reduce((a, b) => a + b, 0) / n
    const meanY = ys.reduce((a, b) => a + b, 0) / n
    const num = xs.reduce((sum, x, i) => sum + (x - meanX) * (ys[i] - meanY), 0)
    const denX = Math.sqrt(xs.reduce((sum, x) => sum + (x - meanX) ** 2, 0))
    const denY = Math.sqrt(ys.reduce((sum, y) => sum + (y - meanY) ** 2, 0))
    if (denX === 0 || denY === 0) return null
    return num / (denX * denY)
}

const QA_GRADE_CONFIG = [
    { minScore: QA_SCORE_THRESHOLDS.GREEN, grade: 'Reliable', range: '≥ 85', color: '#10B981' },
    { minScore: QA_SCORE_THRESHOLDS.YELLOW, grade: 'Acceptable', range: '70–84', color: '#F59E0B' },
    { minScore: 0, grade: 'At Risk', range: '< 70', color: '#EF4444' },
]

function getQaGrade(score) {
    return QA_GRADE_CONFIG.find((g) => score >= g.minScore) ?? QA_GRADE_CONFIG[QA_GRADE_CONFIG.length - 1]
}

const QUALITY_METRICS = [
    { key: 'completedCount', label: 'Stories Completed', color: '#10B981', axis: 'left', dash: '0', icon: '✅' },
    { key: 'bugsInSprint', label: 'Defects Opened', color: '#F97316', axis: 'left', dash: '4 4', icon: '🐛' },
    { key: 'rolloverCount', label: 'Rollover Stories', color: '#A855F7', axis: 'right', dash: '4 2', icon: '⏩' },
    { key: 'aiCompletedCount', label: 'AI Completed', color: '#06B6D4', axis: 'right', dash: '4 2', icon: '🤖' },
    { key: 'avgCycleTimeDays', label: 'Avg Dev Time (d)', color: '#6366F1', axis: 'right', dash: '2 2', icon: '⏱️' },
]
const RELEASE_METRICS = [
    { key: 'hotfixCount', label: 'Hotfix Releases', color: '#EF4444', axis: 'right', dash: '6 2', icon: '🔥' },
    { key: 'patchCount', label: 'Patch Releases', color: '#3B82F6', axis: 'right', dash: '6 2', icon: '🩹' },
    { key: 'scheduledCount', label: 'Scheduled Releases', color: '#10B981', axis: 'right', dash: '4 4', icon: '🚀' },
]
const METRICS = [...QUALITY_METRICS, ...RELEASE_METRICS]

const OVERLAY_OPTIONS = [
    { value: 'none', label: 'None', desc: '' },
    {
        value: 'qaScore',
        label: '📋 QA Scores',
        desc: 'Color-coded quality grade per sprint based on releases, defects, rollovers, and delivery rate',
    },
    { value: 'risk', label: '⚠️ Risk Zones', desc: 'Shades sprints with above-average defects or QA failures' },
    { value: 'lag', label: '⏱️ Lag Effect', desc: 'Also shades the following sprint as the predicted hotfix window' },
]

function formatEndDate(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

function CustomXTick({ x, y, payload }) {
    const sprintNum = payload?.value ? parseInt(payload.value.replace(/^S/, ''), 10) : NaN
    const isoDate = isNaN(sprintNum) ? '' : (SPRINT_END_DATES[sprintNum] ?? '')
    const dateStr = isoDate ? formatEndDate(isoDate) : ''
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={12} textAnchor="middle" fill={PULSE_COLORS.textMuted} fontSize={12}>
                {payload?.value}
            </text>
            {dateStr && (
                <text x={0} y={0} dy={26} textAnchor="middle" fill={PULSE_COLORS.textSubtle} fontSize={10}>
                    {dateStr}
                </text>
            )}
        </g>
    )
}

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null
    const hotfixDetails = payload[0]?.payload?.hotfixDetails ?? []
    const patchDetails = payload[0]?.payload?.patchDetails ?? []
    const sorted = [...payload].sort((a, b) => b.value - a.value)
    return (
        <div
            style={{
                backgroundColor: PULSE_COLORS.bgInner,
                border: `1px solid ${PULSE_COLORS.border}`,
                borderRadius: '10px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                minWidth: 220,
                maxWidth: 300,
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    backgroundColor: PULSE_COLORS.cardBg,
                    borderBottom: `1px solid ${PULSE_COLORS.border}`,
                    padding: '8px 12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <span style={{ fontSize: 13, fontWeight: 700, color: PULSE_COLORS.text }}>Sprint {label}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: PULSE_COLORS.textMuted }}>📉 Sprint Quality</span>
            </div>
            <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {sorted.map((e) => (
                    <div
                        key={e.dataKey}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: e.color,
                                    flexShrink: 0,
                                }}
                            />
                            <span style={{ fontSize: 12, color: PULSE_COLORS.text }}>{e.name}</span>
                        </div>
                        <span
                            style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: e.value > 0 ? e.color : PULSE_COLORS.textSubtle,
                            }}
                        >
                            {e.value}
                        </span>
                    </div>
                ))}
            </div>
            {hotfixDetails.length > 0 && (
                <div style={{ borderTop: `1px solid ${PULSE_COLORS.border}`, padding: '8px 12px', backgroundColor: '#7f1d1d33' }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#FCA5A5', marginBottom: 4 }}>🔥 Hotfixes</p>
                    {hotfixDetails.map((d, i) => (
                        <p key={i} style={{ fontSize: 11, color: PULSE_COLORS.textMuted, lineHeight: '1.4', margin: 0 }}>
                            • {d}
                        </p>
                    ))}
                </div>
            )}
            {patchDetails.length > 0 && (
                <div style={{ borderTop: `1px solid ${PULSE_COLORS.border}`, padding: '8px 12px', backgroundColor: '#1e3a8a33' }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#93C5FD', marginBottom: 4 }}>🩹 Patches</p>
                    {patchDetails.map((d, i) => (
                        <p key={i} style={{ fontSize: 11, color: PULSE_COLORS.textMuted, lineHeight: '1.4', margin: 0 }}>
                            • {d}
                        </p>
                    ))}
                </div>
            )}
        </div>
    )
}

export default function QaDiagnosticsChart({ sprints }) {
    const [visible, setVisible] = useState(
        new Set(['completedCount', 'bugsInSprint', 'hotfixCount', 'patchCount', 'scheduledCount', 'rolloverCount', 'aiCompletedCount'])
    )
    const [overlay, setOverlay] = useState('none')

    const data = sprints.map((s) => ({
        label: `S${s.sprintNumber}`,
        endDate: s.endDate,
        completedCount: s.completedCount,
        bugsInSprint: s.bugsInSprint,
        hotfixCount: s.hotfixCount,
        hotfixDetails: s.hotfixDetails,
        patchCount: s.patchCount,
        patchDetails: s.patchDetails,
        scheduledCount: s.scheduledCount,
        rolloverCount: s.rolloverCount,
        aiCompletedCount: s.aiCompletedCount,
        avgCycleTimeDays: s.avgCycleTimeDays,
    }))

    const correlation = useMemo(() => {
        const valid = sprints.filter((s) => s.completedCount > 0)
        if (valid.length < 3) return null
        const aiAdoption = valid.map((s) => s.aiCompletedCount / s.completedCount)
        const defectDensity = valid.map((s) => s.bugsInSprint / s.completedCount)
        const r = pearsonR(aiAdoption, defectDensity)
        return r !== null ? { r: Math.round(r * 100) / 100, n: valid.length } : null
    }, [sprints])

    const toggleMetric = (key) =>
        setVisible((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })

    // Compute shaded reference areas for risk/lag overlays
    const referenceAreas = useMemo(() => {
        if (overlay === 'none' || overlay === 'qaScore' || data.length < 2) return []
        const avgBugs = data.reduce((s, d) => s + d.bugsInSprint, 0) / data.length
        const avgRollovers = data.reduce((s, d) => s + d.rolloverCount, 0) / data.length
        const avgHotfixes = data.reduce((s, d) => s + d.hotfixCount, 0) / data.length

        const riskAreas = []
        const lagAreas = []

        for (let i = 0; i < data.length - 1; i++) {
            const isAtRisk = data[i].bugsInSprint > avgBugs
            if (!isAtRisk) continue
            const riskY2 = Math.max(data[i].bugsInSprint, 1)
            riskAreas.push({
                x1: data[i].label,
                x2: data[i + 1].label,
                fill: '#EF4444',
                type: 'risk',
                y1: 0,
                y2: riskY2,
                yAxisId: 'left',
            })
            if (overlay === 'lag') {
                let lagEndIdx = i + 1
                for (let j = i + 1; j < data.length - 1; j++) {
                    if (data[j].rolloverCount > avgRollovers || data[j].hotfixCount > avgHotfixes) {
                        lagEndIdx = j + 1
                    } else {
                        break
                    }
                }
                const lagSpan = data.slice(i + 1, Math.min(lagEndIdx + 1, data.length))
                const lagY2 = Math.max(...lagSpan.map((s) => Math.max(s.rolloverCount, s.hotfixCount)), 1)
                const lagX2Label = lagEndIdx < data.length ? data[lagEndIdx].label : data[data.length - 1].label
                lagAreas.push({
                    x1: data[i + 1].label,
                    x2: lagX2Label,
                    fill: '#F59E0B',
                    type: 'lag',
                    y1: 0,
                    y2: lagY2,
                    yAxisId: 'right',
                })
            }
        }
        return [...lagAreas, ...riskAreas]
    }, [data, overlay])

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px', gap: '12px', flexWrap: 'wrap' }}>
                <div>
                    <h3 style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '16px' }}>📉 QA Diagnostics</h3>
                    <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', marginTop: '2px' }}>
                        Overlay quality signals to understand what&apos;s driving changes sprint over sprint
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <span
                        style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            color: PULSE_COLORS.textMuted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        }}
                    >
                        Overlays
                    </span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        {OVERLAY_OPTIONS.map((o) => (
                            <button
                                key={o.value}
                                onClick={() => setOverlay(o.value)}
                                title={o.desc}
                                style={{
                                    padding: '4px 10px',
                                    borderRadius: '999px',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    border: `1px solid ${overlay === o.value ? PULSE_COLORS.primary : PULSE_COLORS.border}`,
                                    backgroundColor: overlay === o.value ? PULSE_COLORS.primary + '33' : 'transparent',
                                    color: overlay === o.value ? PULSE_COLORS.text : PULSE_COLORS.textMuted,
                                    cursor: 'pointer',
                                }}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                {QUALITY_METRICS.map((m) => (
                    <MetricPill key={m.key} m={m} active={visible.has(m.key)} onClick={() => toggleMetric(m.key)} />
                ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                {RELEASE_METRICS.map((m) => (
                    <MetricPill key={m.key} m={m} active={visible.has(m.key)} onClick={() => toggleMetric(m.key)} />
                ))}
            </div>

            {overlay === 'qaScore' && (
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '8px',
                        fontSize: '11px',
                        color: PULSE_COLORS.textMuted,
                    }}
                >
                    <span style={{ fontWeight: 600, color: PULSE_COLORS.text }}>QA Score:</span>
                    {QA_GRADE_CONFIG.map((g) => (
                        <span key={g.grade} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '2px',
                                    backgroundColor: g.color,
                                    opacity: 0.5,
                                    border: `1px solid ${g.color}`,
                                }}
                            />
                            <span style={{ color: g.color, fontWeight: 600 }}>{g.grade}</span>
                            <span>{g.range}</span>
                        </span>
                    ))}
                </div>
            )}
            {(overlay === 'risk' || overlay === 'lag') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px', fontSize: '11px', color: PULSE_COLORS.textMuted }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#EF4444', opacity: 0.4 }} />
                        At-risk sprint (high defects / QA failures)
                    </span>
                    {overlay === 'lag' && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#F59E0B', opacity: 0.6 }} />
                            Predicted impact window
                        </span>
                    )}
                </div>
            )}

            <div style={{ fontSize: '11px', color: PULSE_COLORS.textSubtle, marginBottom: '8px' }}>
                ← Left axis: Stories Completed, Defects &nbsp;|&nbsp; Right axis →: Hotfixes, Patches, Scheduled,
                Rollovers, AI Completed, Avg Dev Time
            </div>

            {correlation &&
                (() => {
                    const { r, n } = correlation
                    const absR = Math.abs(r)
                    const direction = r < 0 ? 'fewer' : 'more'
                    const strength = absR >= 0.6 ? 'strong' : absR >= 0.3 ? 'moderate' : 'weak'
                    const hasSignal = absR >= 0.3
                    const color = !hasSignal ? PULSE_COLORS.textMuted : r < 0 ? '#34d399' : '#f87171'
                    const label = hasSignal
                        ? `${strength.charAt(0).toUpperCase() + strength.slice(1)} correlation — AI-heavy sprints tend to have ${direction} defects per story`
                        : 'No clear correlation between AI adoption and defect density'
                    return (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '12px',
                                padding: '8px 12px',
                                borderRadius: '8px',
                                fontSize: '12px',
                                backgroundColor: PULSE_COLORS.bgInner,
                                border: `1px solid ${PULSE_COLORS.border}`,
                                flexWrap: 'wrap',
                            }}
                        >
                            <span style={{ color, fontWeight: 700, fontSize: 13 }}>
                                r = {r > 0 ? '+' : ''}
                                {r}
                            </span>
                            <span style={{ color: PULSE_COLORS.textMuted }}>AI Adoption ↔ Defect Density</span>
                            <span style={{ color: PULSE_COLORS.textSubtle }}>·</span>
                            <span style={{ color }}>{label}</span>
                            <span style={{ color: PULSE_COLORS.textSubtle, marginLeft: 'auto' }}>{n} sprints</span>
                        </div>
                    )
                })()}

            <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={data} margin={{ bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={PULSE_COLORS.border} />
                    <XAxis dataKey="label" tick={<CustomXTick />} axisLine={false} tickLine={false} height={60} />
                    <YAxis yAxisId="left" tick={{ fill: PULSE_COLORS.textMuted, fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: PULSE_COLORS.textMuted, fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} wrapperStyle={{ opacity: 1, zIndex: 50 }} />
                    <Legend
                        formatter={(value) => (
                            <span style={{ color: PULSE_COLORS.textMuted, fontSize: '11px' }}>{value}</span>
                        )}
                    />
                    {overlay === 'qaScore' &&
                        sprints.map((sprint, i) => {
                            const grade = getQaGrade(computeQaScore(sprint))
                            return (
                                <ReferenceLine
                                    key={`qa-${i}`}
                                    x={data[i]?.label}
                                    yAxisId="left"
                                    stroke={grade.color}
                                    strokeWidth={28}
                                    strokeOpacity={0.25}
                                />
                            )
                        })}
                    {referenceAreas.map((area, i) => (
                        <ReferenceArea
                            key={i}
                            x1={area.x1}
                            x2={area.x2}
                            y1={area.y1}
                            y2={area.y2}
                            yAxisId={area.yAxisId}
                            fill={area.fill}
                            fillOpacity={area.type === 'lag' ? 0.4 : 0.3}
                            stroke={area.type === 'lag' ? '#F59E0B' : undefined}
                            strokeOpacity={area.type === 'lag' ? 0.6 : 0}
                            strokeWidth={1}
                        />
                    ))}
                    {METRICS.map((m) =>
                        visible.has(m.key) ? (
                            <Line
                                key={m.key}
                                yAxisId={m.axis}
                                type="monotone"
                                dataKey={m.key}
                                name={m.label}
                                stroke={m.color}
                                strokeWidth={2}
                                strokeDasharray={m.dash === '0' ? undefined : m.dash}
                                dot={{ r: 3, fill: m.color }}
                                activeDot={{ r: 5 }}
                            />
                        ) : null
                    )}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    )
}

function MetricPill({ m, active, onClick }) {
    return (
        <button
            onClick={onClick}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 500,
                border: `1px solid ${active ? m.color : PULSE_COLORS.border}`,
                backgroundColor: active ? m.color : 'transparent',
                color: active ? '#fff' : PULSE_COLORS.textMuted,
                cursor: 'pointer',
            }}
        >
            <span>{m.icon}</span>
            <span>{m.label}</span>
            {m.dash !== '0' && active && <span style={{ opacity: 0.7 }}>- - -</span>}
        </button>
    )
}
